import { useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Minus, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function KeywordsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ keyword: "", ville: "", target_url: "", position: "", search_volume: "" });
  const [adding, setAdding] = useState(false);
  const [refreshing, setRefreshing] = useState("");

  const load = () => api.get("/admin/keywords").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const refreshOne = async (kw) => {
    setRefreshing(kw.id);
    try {
      await api.post(`/admin/keywords/${kw.id}/refresh`);
      toast.success(`Position actualisée pour « ${kw.keyword} »`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setRefreshing(""); }
  };
  const refreshAll = async () => {
    setRefreshing("all");
    try {
      await api.post("/admin/keywords/refresh-all");
      toast.success("Positions actualisées via Google (SerpApi)");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setRefreshing(""); }
  };

  const add = async () => {
    if (!form.keyword) { toast.error("Mot-clé requis"); return; }
    setAdding(true);
    try {
      await api.post("/admin/keywords", {
        keyword: form.keyword, ville: form.ville, target_url: form.target_url,
        position: form.position ? Number(form.position) : null,
        search_volume: form.search_volume ? Number(form.search_volume) : null,
      });
      setForm({ keyword: "", ville: "", target_url: "", position: "", search_volume: "" });
      toast.success("Mot-clé ajouté"); load();
    } catch { toast.error("Erreur"); } finally { setAdding(false); }
  };

  const updatePos = async (kw) => {
    const v = window.prompt(`Nouvelle position pour « ${kw.keyword} » :`, kw.position ?? "");
    if (v === null || v === "") return;
    await api.put(`/admin/keywords/${kw.id}`, { position: Number(v) });
    toast.success("Position mise à jour"); load();
  };
  const remove = async (kw) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/admin/keywords/${kw.id}`); load(); };

  const trend = (kw) => {
    if (kw.previous_position == null || kw.position == null) return <Minus size={15} className="text-ink/30" />;
    if (kw.position < kw.previous_position) return <span className="inline-flex items-center gap-0.5 text-green-600 text-xs font-bold"><ArrowUp size={14} />{kw.previous_position - kw.position}</span>;
    if (kw.position > kw.previous_position) return <span className="inline-flex items-center gap-0.5 text-red-600 text-xs font-bold"><ArrowDown size={14} />{kw.position - kw.previous_position}</span>;
    return <Minus size={15} className="text-ink/30" />;
  };

  const field = "w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary";

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black text-ink">Suivi des positions SEO</h1>
          <p className="text-ink/50 mt-1">Positions Google réelles récupérées automatiquement via SerpApi (Google.fr).</p>
        </div>
        <button onClick={refreshAll} disabled={refreshing === "all"} data-testid="kw-refresh-all"
          className="inline-flex items-center justify-center gap-2 bg-ink text-white px-5 py-2.5 rounded-full font-bold hover:bg-primary transition disabled:opacity-60">
          {refreshing === "all" ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />} Actualiser via Google
        </button>
      </div>

      <div className="mt-6 bg-card border border-border rounded-xl p-4 grid grid-cols-1 sm:grid-cols-6 gap-3 items-end">
        <div className="sm:col-span-2"><label className="text-xs font-semibold text-ink/60">Mot-clé *</label><input data-testid="kw-keyword" className={field} value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value })} /></div>
        <div><label className="text-xs font-semibold text-ink/60">Ville</label><input className={field} value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} /></div>
        <div><label className="text-xs font-semibold text-ink/60">Position</label><input type="number" className={field} value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} /></div>
        <div><label className="text-xs font-semibold text-ink/60">Volume/mois</label><input type="number" className={field} value={form.search_volume} onChange={(e) => setForm({ ...form, search_volume: e.target.value })} /></div>
        <button onClick={add} disabled={adding} data-testid="kw-add" className="inline-flex items-center justify-center gap-2 bg-primary text-white font-bold py-2.5 rounded-lg disabled:opacity-60">{adding ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Ajouter</button>
      </div>

      <div className="mt-6 bg-card border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-muted/50 text-left text-ink/60"><tr>
            <th className="px-4 py-3 font-semibold">Mot-clé</th><th className="px-4 py-3 font-semibold">Ville</th>
            <th className="px-4 py-3 font-semibold">Position</th><th className="px-4 py-3 font-semibold">Évolution</th>
            <th className="px-4 py-3 font-semibold">Volume</th><th className="px-4 py-3 font-semibold text-right">Actions</th>
          </tr></thead>
          <tbody>
            {items.map((kw) => (
              <tr key={kw.id} className="border-t border-border" data-testid={`kw-row-${kw.id}`}>
                <td className="px-4 py-3 font-medium text-ink">{kw.keyword}</td>
                <td className="px-4 py-3 text-ink/60">{kw.ville || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`font-heading font-black ${kw.position == null ? "text-ink/40" : kw.position <= 3 ? "text-green-600" : kw.position <= 10 ? "text-primary" : "text-ink/60"}`}>
                    {kw.position != null ? `#${kw.position}` : kw.last_checked ? "Non classé" : "—"}
                  </span>
                  {kw.last_checked && <span className="block text-[10px] text-ink/40">{new Date(kw.last_checked).toLocaleDateString("fr-FR")}</span>}
                </td>
                <td className="px-4 py-3">{trend(kw)}</td>
                <td className="px-4 py-3 text-ink/60">{kw.search_volume ?? "—"}</td>
                <td className="px-4 py-3"><div className="flex items-center justify-end gap-2">
                  <button onClick={() => refreshOne(kw)} disabled={refreshing === kw.id} data-testid={`kw-refresh-${kw.id}`} title="Actualiser via Google" className="p-1.5 border border-border rounded-lg hover:border-primary text-ink/70 disabled:opacity-50">{refreshing === kw.id ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={15} />}</button>
                  <button onClick={() => updatePos(kw)} className="text-xs border border-border rounded-lg px-2 py-1 hover:border-primary" title="Saisie manuelle">Manuel</button>
                  <button onClick={() => remove(kw)} className="p-1.5 text-red-600"><Trash2 size={15} /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!items.length && <p className="p-8 text-center text-ink/40">Aucun mot-clé suivi.</p>}
      </div>
    </div>
  );
}
